import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard } from 'lucide-react';
import './Dashboard.css';
import logo from '../images/chaimate-logo-main.svg';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.chaimate.club';

function formatCredits(n) {
  if (n == null) return '—';
  return n.toLocaleString('en-IN');
}

export default function Dashboard() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('dash_api_key') || '');
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoLoaded, setAutoLoaded] = useState(false);

  const fetchAccount = useCallback(async (key) => {
    if (!key.trim()) {
      setError('Please enter an API key.');
      return;
    }
    setLoading(true);
    setError('');
    setAccount(null);
    try {
      const res = await fetch(`${BASE_URL}/api-keys/me`, {
        headers: {
          'X-API-Key': key.trim(),
          'Content-Type': 'application/json',
        },
      });
      const json = await res.json();
      if (json.data) {
        setAccount(json.data);
        localStorage.setItem('dash_api_key', key.trim());
      } else {
        setError(json.error_message || 'Invalid API key or request failed.');
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (apiKey.trim() && !autoLoaded) {
      setAutoLoaded(true);
      fetchAccount(apiKey);
    }
  }, [apiKey, autoLoaded, fetchAccount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchAccount(apiKey);
  };

  const handleClear = () => {
    setApiKey('');
    setAccount(null);
    setError('');
    localStorage.removeItem('dash_api_key');
  };

  const credits = account?.credits ?? null;
  const isLow = credits != null && credits <= 50;
  const isExhausted = credits != null && credits <= 0;

  return (
    <div className="dash">
      {/* Sidebar */}
      <aside className="dash-sidebar">
        <div className="dash-sidebar-brand">
          <img src={logo} alt="Chaimate" className="dash-sidebar-logo" />
          <span className="dash-wordmark">Chaimate</span>
        </div>
        <nav className="dash-sidebar-nav">
          <button type="button" className="dash-nav-item is-active">
            <LayoutDashboard size={18} strokeWidth={2} />
            <span>Credits</span>
          </button>
        </nav>
      </aside>

      {/* Main */}
      <div className="dash-main">
        <header className="dash-topbar">
          <h1 className="dash-topbar-title">Credits</h1>
        </header>

        <div className="dash-content">
          {/* API Key Input — shown only before account loads */}
          {!account && (
            <section className="dash-section">
              <h2 className="dash-section-title">Connect your account</h2>
              <p className="dash-section-sub">Enter your API key to view remaining credits.</p>
              <form className="dash-key-form" onSubmit={handleSubmit}>
                <input
                  className="dash-key-input"
                  type="text"
                  placeholder="synth_YOUR_API_KEY"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                />
                <button
                  className="dash-key-btn"
                  type="submit"
                  disabled={loading || !apiKey.trim()}
                >
                  {loading ? 'Checking…' : 'Check'}
                </button>
              </form>
              {error && <div className="dash-error">{error}</div>}
            </section>
          )}

          {/* Credits Display */}
          {account && (
            <>
              {/* Balance card */}
              <div className={`dash-balance ${isExhausted ? 'is-exhausted' : isLow ? 'is-low' : ''}`}>
                <div className="dash-balance-top">
                  <div className="dash-balance-label">Available Queries</div>
                  <div className="dash-balance-number">{formatCredits(credits)}</div>
                </div>
                <div className="dash-balance-bottom">
                  <span>Prefix: <strong>{account.company_prefix || '—'}</strong></span>
                  <span className={`dash-badge ${account.is_active ? 'is-active' : 'is-inactive'}`}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Account details table */}
              <section className="dash-section">
                <h2 className="dash-section-title">Account Details</h2>
                <div className="dash-details">
                  <div className="dash-detail-row">
                    <span className="dash-detail-key">Company</span>
                    <span className="dash-detail-val">{account.company_name || '—'}</span>
                  </div>
                  <div className="dash-detail-row">
                    <span className="dash-detail-key">Prefix</span>
                    <span className="dash-detail-val"><code>{account.company_prefix || '—'}</code></span>
                  </div>
                  <div className="dash-detail-row">
                    <span className="dash-detail-key">User ID Format</span>
                    <span className="dash-detail-val"><code>{account.user_id_format || '—'}</code></span>
                  </div>
                  {account.user_id_example && (
                    <div className="dash-detail-row">
                      <span className="dash-detail-key">Example</span>
                      <span className="dash-detail-val"><code>{account.user_id_example}</code></span>
                    </div>
                  )}
                  <div className="dash-detail-row">
                    <span className="dash-detail-key">Cost per call</span>
                    <span className="dash-detail-val">1 credit per <code>/chat/external/reply</code></span>
                  </div>
                </div>
              </section>

              <button type="button" className="dash-disconnect" onClick={handleClear}>
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
